import { useRef, useState } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { CheckLg, LockFill, XLg } from 'react-bootstrap-icons';

import FormLoader from '../FormLoader/FormLoader';
import Label from '../label/label';
import ModalFooter from '../modal/modal-footer';
import FormikFormTextArea from '../FormikFormHandlers/FormikFormTextArea';
import FormikFormSelect from '../FormikFormHandlers/FormikFormSelect';
import FormikFormInput from '../FormikFormHandlers/FormikFormInput';
import { NonConformityValidationSchema } from '../FormikFormHandlers/FormikValidationSchema';

import { IOption, IReactSelectOption } from '../../models/ICommon';
import { formatMessage } from '../../translations/format-message';
import { DateFormatNew } from '../../constants/date-picker-constant';
import { IAddNCRequest } from '../../models/forms/IAddNonConformity';
import { TEXTAREA_ROW_LENGTH } from '../../constants/constants';
import { ReportedByOptions } from '../../constants/options.constant';
import { IGetNonConformance } from '../../models/response/IAddedNonConformanceResponse';
import authStore from '../../core/stores/auth-store';
import { UserTypeEnum } from '../../constants/enums';
import NonConformityStatusEnum from '../../constants/enums/non-conformance-status-enum';

import './add-update-non-conformity-form.css';
import { PlusIcon } from '../svgs/svgs';

const customStyles = {
  control: (base: any) => ({
    ...base,
    boxShadow:
      'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
    borderRadius: '8px',
    border: '1px solid var(--bs-border-color)',
  }),

  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isFocused ? '#33439c' : isSelected ? '#33439c' : null,
    color: isFocused ? '#fff' : isSelected ? '#fff' : '#000',
  }),

  // Developer Comment: Render the menu in a portal (see menuPortalTarget below) so it
  // escapes the scrollable/overflow crf-card stacking context and always paints above
  // the controls/buttons of subsequent Non-Conformance rows instead of behind them.
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

// Developer Comment: Read-only presentational field used to replace disabled form
// controls once a section is locked (submitted/approved), so locked data reads as
// plain text in a themed card instead of greyed-out disabled inputs.
interface IReadOnlyFieldProps {
  label: string;
  value?: string | number | null;
  multiline?: boolean;
}

const ReadOnlyField = ({ label, value, multiline }: IReadOnlyFieldProps) => (
  <div className="crf-readonly-field">
    <label className="crf-readonly-label">{label}</label>
    <div
      className={
        multiline
          ? 'crf-readonly-value crf-readonly-value-multiline'
          : 'crf-readonly-value'
      }
    >
      {value || value === 0 ? value : '-'}
    </div>
  </div>
);

// Developer Comment: Presentational building blocks for the "Non-Conformity Work
// History" panel, restyled as a support-ticket / conversation timeline. These are
// pure/reusable — they only render the fields handed to them, no data logic lives
// here; the history list mapping and its conditions remain in the parent component.

// Reusable status pill (NG today; keyed so future judgement states can opt into a
// colour without touching the markup).
const STATUS_BADGE_VARIANT_CLASS: Record<string, string> = {
  NG: 'crf-status-badge-danger',
  OK: 'crf-status-badge-success',
};

const StatusBadge = ({ status }: { status: string }) => (
  <span
    className={`crf-status-badge ${
      STATUS_BADGE_VARIANT_CLASS[String(status).toUpperCase()] ||
      'crf-status-badge-neutral'
    }`}
  >
    {status}
  </span>
);

// Centered "date-separator"-style divider carrying the common Work Judgment /
// Work Process info for the record that follows, instead of repeating it as a
// large form section.
interface IWorkInfoDividerProps {
  judgment?: string;
  process?: string;
}

const WorkInfoDivider = ({ judgment, process }: IWorkInfoDividerProps) => (
  <div className="crf-timeline-divider">
    <span className="crf-timeline-divider-line" />
    <span className="crf-timeline-divider-pill">
      <span className="crf-timeline-divider-title">{formatMessage('work_update')}</span>
      {(judgment || process) && (
        <span className="crf-timeline-divider-meta">
          {judgment && <StatusBadge status={judgment} />}
          {judgment && process && <span aria-hidden="true">&bull;</span>}
          {process && (
            <span className="crf-timeline-divider-process">{process}</span>
          )}
        </span>
      )}
    </span>
    <span className="crf-timeline-divider-line" />
  </div>
);

// Operator-side conversation message. The "Operator Content" label lives in the
// header above the bubble (not repeated inside it).
const OperatorMessage = ({ content }: { content: string }) => (
  <div className="crf-msg">
    <div className="crf-msg-header">
      <span className="crf-msg-avatar crf-msg-avatar-operator">O</span>
      <span>{formatMessage('operator_content')}</span>
    </div>
    <div className="crf-msg-bubble crf-msg-bubble-operator">
      <div className="crf-msg-text">{content}</div>
    </div>
  </div>
);

// Admin/support-side response. The "NG Reason" label lives in the header above
// the bubble, and the avatar itself carries the judgement (e.g. "NG") instead of
// a separate status badge repeating it inside the bubble.
interface IAdminResponseProps {
  status: string;
  reason?: string;
}

const AdminResponse = ({ status, reason }: IAdminResponseProps) => (
  <div className="crf-msg crf-msg-align-end">
    <div className="crf-msg-header crf-msg-header-end">
      <span>{formatMessage('ng_reason')}</span>
      <span className="crf-msg-avatar crf-msg-avatar-admin">{status}</span>
    </div>
    <div className="crf-msg-bubble crf-msg-bubble-admin">
      <div className="crf-msg-text">{reason || '-'}</div>
    </div>
  </div>
);

// Wraps one timeline event (divider / operator message / admin response) and
// attaches the connector dot for it via CSS — see .crf-timeline-node in the
// stylesheet.
const TimelineNode = ({
  variant,
  children,
}: {
  variant: 'divider' | 'operator' | 'admin';
  children: React.ReactNode;
}) => (
  <div className={`crf-timeline-node crf-timeline-node-${variant}`}>
    {children}
  </div>
);

// Developer Comment: Define the 4 workflow modes for Non-Conformity processing
export type NonConformityFormMode =
  | 'CREATE'
  | 'APPROVAL'
  | 'OPERATOR_ACTION'
  | 'VERIFICATION';

export interface IAddUpdateNonConformityFormProps {
  id: number;
  initialValues: IAddNCRequest | IGetNonConformance;
  isLoading: boolean;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  allProductProcess: IOption[];
  workProcessOptions?: IOption[];
  productName: string;
  processId: number;
  userName: string;
  trayId: string;
  Kishu: string;
  Katashiki: string;
  processName: string;
  quantity: number;
  workJudgementOptions?: IOption[];
  materialKatashikiOptions: IReactSelectOption[];
  subjectOptions: IReactSelectOption[];
  getItemsBySubject: (subject: string) => Promise<string[]>;
  subjectItemOptions?:
    | Record<number, IReactSelectOption[]>
    | IReactSelectOption[];
  status?: number;
  isBatchMode?: boolean;
  selectedTrays?: any[];
  mode?: NonConformityFormMode;
  processOptions?: IOption[];
}

/**
 * Determine active form mode based on explicit 'mode' prop or backend status:
 * - Status -1 / default -> CREATE
 * - Status 0 / Pending -> APPROVAL
 * - Status 1 / NCApproved or Status 3 / NG -> OPERATOR_ACTION
 * - Status 2 / Verify -> VERIFICATION
 */
const getFormMode = (
  explicitMode?: NonConformityFormMode,
  status?: number,
  isEditingExisting?: boolean
): NonConformityFormMode => {
  if (explicitMode) return explicitMode;

  switch (status) {
    case NonConformityStatusEnum.Pending:
    case 0:
      return 'APPROVAL';

    case NonConformityStatusEnum.NCApproved:
    case 1:
    case NonConformityStatusEnum.NG:
    case 3:
      return 'OPERATOR_ACTION';

    case NonConformityStatusEnum.Verify:
    case 2:
      return 'VERIFICATION';

    case NonConformityStatusEnum.default:
    case -1:
    default:
      return isEditingExisting ? 'APPROVAL' : 'CREATE';
  }
};

/**
 * Helper to calculate initial Formik values from raw initial values and active form mode.
 */
const buildInitialFormValues = (
  initialValues: any,
  formMode: NonConformityFormMode,
  operatorComment: string
) => {
  const rawItems = initialValues?.NonConformanceItems;
  const hasItems = Array.isArray(rawItems) && rawItems.length > 0;

  const formattedItems = hasItems
    ? rawItems.map((item: any) => ({
        Id: item.Id ?? 0,
        Subject: item.Subject ?? '',
        Item: item.Item ?? '',
        BOMInfo: item.BOMInfo ?? '',
        defectiveItemCount: item.DefectiveItemCount ?? item.defectiveItemCount ?? 0,
        defectDetails: item.DefectDetails ?? item.defectDetails ?? '',
        ConfirmedBy: item.ConfirmedBy ?? item.ApprovedBy ?? -1,
      }))
    : [
        {
          Id: 0,
          Subject: '',
          Item: '',
          BOMInfo: '',
          defectiveItemCount: 0,
          defectDetails: '',
          ConfirmedBy: -1,
        },
      ];

  return {
    ...initialValues,
    ReturnProcessId:
      initialValues?.ReturnProcessId ||
      initialValues?.DetectionProcessId ||
      '',
    WorkComment: formMode === 'OPERATOR_ACTION' ? '' : operatorComment,
    NGReason:
      initialValues?.NGReason ??
      initialValues?.ng_reason ??
      '',
    WorkJudgement: initialValues?.WorkJudgement ?? -1,
    WorkProcess:
      initialValues?.WorkProcess ??
      initialValues?.ReturnProcessId ??
      '',
    NonConformanceItems: formattedItems,
  };
};

const AddUpdateNonConformityForm = ({
  id,
  initialValues,
  isLoading,
  submitHandler,
  onModalClosed,
  allProductProcess,
  workProcessOptions = [],
  productName,
  userName,
  trayId,
  Katashiki,
  Kishu,
  processName,
  quantity,
  materialKatashikiOptions,
  subjectOptions,
  getItemsBySubject,
  status,
  mode,
  processOptions = [],
}: IAddUpdateNonConformityFormProps) => {
  const [itemOptions, setItemOptions] = useState<
    Record<number, IReactSelectOption[]>
  >({});

  const handleModalClose = () => {
    setItemOptions({});
    onModalClosed();
  };

  // Track initial item count to prevent disabling newly added rows in approval mode
  const initialNonConformanceItemCount =
    initialValues.NonConformanceItems?.length ?? 0;
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  const isOperatorRole = authStore.getUserType === UserTypeEnum[6];

  // Get current numerical status
  const currentStatus =
    status !== NonConformityStatusEnum.default
      ? status
      : ((initialValues as any)?.Status ?? NonConformityStatusEnum.default);

  // Developer Comment: Non-conformity has cleared approval once its status has
  // moved past Pending — NCApproved, Verify, NG or Complete all count.
  const isApproved = currentStatus >= NonConformityStatusEnum.NCApproved;

  // Get current workflow mode
  const formMode = getFormMode(
    mode,
    currentStatus,
    Boolean((initialValues as any)?.Id && (initialValues as any)?.Id > 0)
  );

  // Stage permissions logic
  const isAddNCAllowed = formMode === 'CREATE' || formMode === 'APPROVAL';
  const isReturnProcessEditable =
    formMode === 'CREATE' || formMode === 'APPROVAL';
  const isOperatorContentEditable = formMode === 'OPERATOR_ACTION';
  const isWorkEvaluationVisible = formMode === 'VERIFICATION';

  // Helper to check if an NC item row should be disabled
  const isItemDisabled = (itemIndex: number, itemId?: number) => {
    if (formMode === 'CREATE') {
      return false;
    }
    if (formMode === 'APPROVAL') {
      const isExistingItem =
        itemIndex < initialNonConformanceItemCount ||
        Boolean(itemId && itemId > 0);
      return isExistingItem;
    }
    return true;
  };

  // Get NC history items from backend response
  const ngHistoryItems: any[] = (initialValues as any)?.NCNGDetailModelItems || [];

  // Legacy fallback if NCNGDetailModelItems array is empty
  const topLevelNGReason =
    (initialValues as any)?.NGReason ||
    (initialValues as any)?.ng_reason ||
    (initialValues as any)?.NgReason;

  const isNGStatus =
    currentStatus === NonConformityStatusEnum.NG ||
    currentStatus === 3 ||
    Boolean(topLevelNGReason);

  // Build history items list for display
  const displayHistoryList: any[] =
    ngHistoryItems.length > 0
      ? ngHistoryItems
      : isNGStatus
        ? [
            {
              IsNG: true,
              NGReason: topLevelNGReason || '',
              ProcessId:
                (initialValues as any)?.WorkProcess ||
                initialValues?.ReturnProcessId,
              ProcessName: (initialValues as any)?.ReturnProcessName,
              WorkerComment:
                (initialValues as any)?.WorkerComment ||
                (initialValues as any)?.WorkComment ||
                (initialValues as any)?.workComment,
            },
          ]
        : [];

  const operatorComment =
    (initialValues as any)?.WorkComment ||
    (initialValues as any)?.workComment ||
    (initialValues as any)?.WorkerComment ||
    '';

  // Helper to resolve process name from ProcessId for display
  const getProcessLabel = (procId: number | string) => {
    if (procId === undefined || procId === null || procId === '') return '';
    const searchId = String(procId).trim();

    const foundPrimary = allProductProcess?.find(
      (p: any) => String(p.id ?? p.value ?? p.ProcessId ?? p.Id) === searchId
    );
    if (foundPrimary) {
      return (
        (foundPrimary as any).label ??
        (foundPrimary as any).name ??
        (foundPrimary as any).ProcessName ??
        foundPrimary.value
      );
    }

    if (processOptions && processOptions.length > 0) {
      const foundFallback = processOptions.find(
        (p: any) => String(p.id ?? p.value ?? p.ProcessId ?? p.Id) === searchId
      );
      if (foundFallback) {
        return (
          (foundFallback as any).label ??
          (foundFallback as any).name ??
          (foundFallback as any).ProcessName ??
          foundFallback.value
        );
      }
    }

    return String(procId);
  };

  // Helper to resolve "Approved By" display label from a ConfirmedBy id
  const getApprovedByLabel = (confirmedBy: number | string) => {
    if (confirmedBy === undefined || confirmedBy === null || confirmedBy === '')
      return '';
    const found = ReportedByOptions.find(
      (option) => String(option.id) === String(confirmedBy)
    );
    return found?.value ?? String(confirmedBy);
  };

  // Helper to resolve BOM Info display label from its stored value
  const getBomInfoLabel = (bomInfo: string) => {
    if (!bomInfo) return '';
    const found = materialKatashikiOptions.find(
      (option) => option.value === bomInfo
    );
    return found?.label ?? bomInfo;
  };

  // Developer Comment: Render right panel once the non-conformity is approved
  // (not merely once there's history/operator content to show) — or in
  // verification mode, kept as a safety net for a mode forced via the `mode`
  // prop ahead of currentStatus reflecting it.
  const hasRightSection = isApproved || isWorkEvaluationVisible;

  // Developer Comment: Whenever the right panel is showing, the Operator Content
  // field lives there as the composer pinned below the conversation instead of
  // the left form panel — even before any history exists yet (e.g. right after
  // approval, before the operator has posted anything).
  const showOperatorContentInHistoryPanel = hasRightSection;

  // Build Formik initial values
  const formInitialValues = buildInitialFormValues(
    initialValues,
    formMode,
    operatorComment
  );

  // Total quantity used for item count validation
  const totalQuantity = Number(quantity || (initialValues as any)?.Quantity || 0);
  const getSaveButtonText = (status: number) => {
    switch (status) {
      case -1:
        return 'Add';
      case 0:
        return 'Approve';
      case 1:
        return 'Complete';
      case 2:
        return 'Verify';
      default:
        return 'Save';
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validateOnBlur={false}
        validationSchema={NonConformityValidationSchema(
          totalQuantity,
          formMode
        )}
        onSubmit={async (values) => {

          // Call parent submit handler with form values
          submitHandler({
            NonConformanceItems: values.NonConformanceItems,
            ReturnProcessId: values.ReturnProcessId,
            WorkComment: values.WorkComment,
            NGReason: values.NGReason,
            WorkJudgement: values.WorkJudgement,
            WorkProcess: values.WorkProcess,
          });
        }}
      >
        {({ values, setFieldValue, errors, touched }) => {
          const handleSubjectChange = async (
            option: IReactSelectOption | null,
            index: number
          ) => {
            const subject = option?.value ?? '';
            setFieldValue(`NonConformanceItems.${index}.Subject`, subject);
            setFieldValue(`NonConformanceItems.${index}.Item`, '');
            setItemOptions((prev) => ({
              ...prev,
              [index]: [],
            }));
            if (!subject) {
              return;
            }
            try {
              const items = await getItemsBySubject(subject);
              setItemOptions((prev) => ({
                ...prev,
                [index]: items.map((item) => ({
                  label: item,
                  value: item,
                })),
              }));
            } catch (error) {
              console.error('Error getting items:', error);
              setItemOptions((prev) => ({
                ...prev,
                [index]: [],
              }));
            }
          };

          return (
            <Form className="user-form crf-form" noValidate>
              {isLoading && <FormLoader loading={isLoading} contained />}
              <Modal.Body className="crf-body pb-0 px-3 pt-2">
                {/* Header Info Row */}
                <div className="row pb-3 align-items-center">
                  <div className="formSet crf-field col">
                    <Label
                      required={false}
                      label="tray_id"
                      className="crf-label text-muted small"
                    />
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      <p className="px-2 py-1 text-white rounded fw-semibold bg-primary w-auto d-inline-block m-0 crf-product-name">
                        {(initialValues as any).TrayId || trayId}
                      </p>
                    </div>
                  </div>

                  <div className="formSet crf-field col">
                    <Label
                      required={false}
                      label="kishu"
                      className="crf-label text-muted small"
                    />
                    <p className="text-dark m-0">
                      {(initialValues as any).Kishu || Kishu || '-'}
                    </p>
                  </div>

                  <div className="formSet crf-field col-3">
                    <Label
                      required={false}
                      label="katashiki"
                      className="crf-label text-muted small"
                    />
                    <p className="text-dark m-0">
                      {(initialValues as any).Katashiki || Katashiki || '-'}
                    </p>
                  </div>

                  <div className="formSet crf-field col">
                    <Label
                      required={false}
                      label="product_id"
                      className="crf-label text-muted small"
                    />
                    <p className="text-dark m-0">
                      {(initialValues as any).ProductId || productName || '-'}
                    </p>
                  </div>

                  <div className="formSet crf-field col">
                    <Label
                      required={false}
                      label="detection_process"
                      className="crf-label text-muted small"
                    />
                    <p className="text-dark m-0">
                      {(initialValues as any).DetectionProcessName ||
                        processName ||
                        '-'}
                    </p>
                  </div>

                  <div className="formSet crf-field col">
                    <Label
                      required={false}
                      label="discovery_date"
                      className="crf-label text-muted small"
                    />
                    <p className="text-dark m-0">
                      {(initialValues as any).DetectedOn &&
                      !isNaN(
                        new Date((initialValues as any).DetectedOn).getTime()
                      ) &&
                      new Date(
                        (initialValues as any).DetectedOn
                      ).getFullYear() > 1970
                        ? format(
                            new Date((initialValues as any).DetectedOn),
                            DateFormatNew
                          )
                        : format(new Date(), DateFormatNew)}
                    </p>
                  </div>

                  <div className="formSet crf-field col">
                    <Label
                      required={false}
                      label="discoverer"
                      className="crf-label text-muted small"
                    />
                    <p className="text-dark m-0">
                      {(initialValues as any).DetectedByName || userName || '-'}
                    </p>
                  </div>
                </div>

                {/* Form Main Body */}
                <div className="row">
                  {/* Left Column: Non-conformance items */}
                  <div
                    className={
                      hasRightSection
                        ? 'crf-card non-conformance-detail col-xl-5 col-xxl-5 d-flex flex-column'
                        : 'crf-card non-conformance-detail col-12 d-flex flex-column'
                    }
                  >
                    {/* Developer Comment: Return Process and the + Add Non-Conformance
                        button are a fixed header above the item cards; only the cards
                        below (.crf-items-scroll) scroll. */}
                    <FieldArray name="NonConformanceItems">
                      {({ push, remove }) => (
                        <>
                          <div className="crf-left-header d-flex align-items-end justify-content-between gap-3">
                            {/* Return Process Field */}
                            <div className="form-group formSet mb-0 flex-grow-1">
                              <Label
                                className="label-style col-form-label"
                                required={true}
                                label="return_process"
                              />
                              {isReturnProcessEditable ? (
                                <Field
                                  name="ReturnProcessId"
                                  as={FormikFormSelect}
                                  options={allProductProcess}
                                  setDefaultValue={true}
                                  className="form-control"
                                />
                              ) : (
                                <div className="crf-readonly-value">
                                  {getProcessLabel(values.ReturnProcessId) || '-'}
                                </div>
                              )}
                            </div>

                            {/* Developer Comment: + Add Non-Conformance button is shown only when permitted by active mode */}
                            {isAddNCAllowed && (
                              <button
                                type="button"
                                className="btn btn-secondary flex-shrink-0"
                                disabled={(() => {
                                  const lastItem =
                                    values.NonConformanceItems[
                                      values.NonConformanceItems.length - 1
                                    ];
                                  return (
                                    !lastItem.Subject ||
                                    !lastItem.Item ||
                                    // !lastItem.BOMInfo ||
                                    !lastItem.defectDetails ||
                                    !lastItem.defectiveItemCount ||
                                    lastItem.ConfirmedBy == null ||
                                    Number(lastItem.ConfirmedBy) === -1
                                  );
                                })()}
                                onClick={() => {
                                  const newIndex =
                                    values.NonConformanceItems.length;
                                  push({
                                    Subject: '',
                                    Item: '',
                                    BOMInfo: '',
                                    defectiveItemCount: 0,
                                    defectDetails: '',
                                    ConfirmedBy: -1,
                                  });
                                  requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                      cardRefs.current[newIndex]?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                      });
                                    });
                                  });
                                }}
                              >
                                <PlusIcon /> {formatMessage('add_non_conformity')}
                              </button>
                            )}
                          </div>

                          <div className="crf-items-scroll">
                          {values.NonConformanceItems.map((item, index) => {
                            // Developer Comment: Determine if this specific item is disabled
                            const itemDisabled = isItemDisabled(index, item.Id);
                            const itemErrors: any =
                              (errors as any)?.NonConformanceItems?.[index] || {};
                            const itemTouched: any =
                              (touched as any)?.NonConformanceItems?.[index] || {};
                            return (
                              <div
                                className="col-12"
                                key={index}
                              >
                                <div
                                  ref={(el) => {
                                    cardRefs.current[index] = el;
                                  }}
                                  className={
                                    itemDisabled
                                      ? 'crf-card mt-2 position-relative card card-body small crf-card-readonly border'
                                      : 'crf-card mt-2 position-relative card card-body small bg-light-primary border'
                                  }
                                >
                                  {itemDisabled ? (
                                    <>
                                      {/* Developer Comment: Item is locked (already submitted) -
                                          show its values as plain, well-styled read-only text
                                          instead of disabled form controls. */}
                                      <span className="badge bg-secondary crf-locked-badge">
                                        <LockFill className="me-1" size={10} />
                                        {formatMessage('submitted')}
                                      </span>
                                      {hasRightSection ? (
                                        <div className="row g-2">
                                          <div className="col-6">
                                            <ReadOnlyField
                                              label={formatMessage('subject')}
                                              value={item.Subject}
                                            />
                                          </div>
                                          <div className="col-6">
                                            <ReadOnlyField
                                              label={formatMessage('item')}
                                              value={item.Item}
                                            />
                                          </div>
                                          <div className="col-12">
                                            <ReadOnlyField
                                              label={formatMessage('bom_info')}
                                              value={getBomInfoLabel(item.BOMInfo)}
                                            />
                                          </div>
                                          <div className="col-12 d-flex align-items-center gap-4 crf-readonly-meta-row">
                                            <div className="d-flex align-items-center gap-2">
                                              <span className="crf-readonly-label mb-0">
                                                {formatMessage('approved_by')}
                                              </span>
                                              <span className="badge bg-primary crf-approved-badge">
                                                {formatMessage(getApprovedByLabel(item.ConfirmedBy)) || '-'}
                                              </span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <span className="crf-readonly-label mb-0">
                                                {formatMessage('defective_item_count')}
                                              </span>
                                              <span className="fw-semibold text-dark">
                                                {item.defectiveItemCount ?? 0} /{' '}
                                                {quantity ||
                                                  (initialValues as any).Quantity ||
                                                  0}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        // Developer Comment: Right section hidden (left panel
                                        // full width) — Subject/Item/BOM Info/Approved By/
                                        // Defective Item Count all fit in a single row instead
                                        // of wrapping across two.
                                        <div className="row g-2">
                                          <div className="col">
                                            <ReadOnlyField
                                              label={formatMessage('subject')}
                                              value={item.Subject}
                                            />
                                          </div>
                                          <div className="col">
                                            <ReadOnlyField
                                              label={formatMessage('item')}
                                              value={item.Item}
                                            />
                                          </div>
                                          <div className="col">
                                            <ReadOnlyField
                                              label={formatMessage('bom_info')}
                                              value={getBomInfoLabel(item.BOMInfo)}
                                            />
                                          </div>
                                          <div className="col-auto">
                                            <div className="crf-readonly-field">
                                              <label className="crf-readonly-label">
                                                {formatMessage('approved_by')}
                                              </label>
                                              <div className="crf-readonly-value">
                                                <span className="badge bg-primary crf-approved-badge">
                                                  {formatMessage(getApprovedByLabel(item.ConfirmedBy)) || '-'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-auto">
                                            <ReadOnlyField
                                              label={formatMessage('defective_item_count')}
                                              value={`${item.defectiveItemCount ?? 0} / ${
                                                quantity ||
                                                (initialValues as any).Quantity ||
                                                0
                                              }`}
                                            />
                                          </div>
                                        </div>
                                      )}

                                      <div className="row g-2">
                                        <div className="col-12">
                                          <div className="crf-defect-details-card">
                                            <label className="crf-readonly-label mb-0">
                                              {formatMessage('defect_details')}
                                            </label>
                                            <div className="crf-defect-details-text">
                                              {item.defectDetails || '-'}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="row">
                                        {/* Subject */}
                                        <div className="form-group formSet col-4">
                                          <Label
                                            className="label-style col-form-label"
                                            label="subject"
                                            required
                                          />
                                          <Select
                                            styles={customStyles}
                                            className="select-did-process"
                                            classNamePrefix="react-select"
                                            classNames={{
                                              option: (d) =>
                                                `our-option ${
                                                  d.isSelected ? 'active' : ''
                                                }`,
                                            }}
                                            name={`NonConformanceItems.${index}.Subject`}
                                            options={subjectOptions}
                                            value={
                                              subjectOptions.find(
                                                (option) =>
                                                  option.value ===
                                                  values.NonConformanceItems[
                                                    index
                                                  ].Subject
                                              ) || null
                                            }
                                            onChange={(
                                              option: IReactSelectOption | null
                                            ) =>
                                              handleSubjectChange(option, index)
                                            }
                                            placeholder={formatMessage(
                                              'please_select'
                                            )}
                                            isClearable
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                          />
                                          {itemTouched.Subject &&
                                            itemErrors.Subject && (
                                              <span className="text-danger errorMessage">
                                                {formatMessage(
                                                  itemErrors.Subject
                                                )}
                                              </span>
                                            )}
                                        </div>

                                        {/* Item */}
                                        <div className="form-group formSet col-4">
                                          <Label
                                            className="label-style col-form-label"
                                            label="item"
                                            required
                                          />
                                          <Select
                                            styles={customStyles}
                                            className="select-did-process"
                                            classNamePrefix="react-select"
                                            classNames={{
                                              option: (d) =>
                                                `our-option ${
                                                  d.isSelected ? 'active' : ''
                                                }`,
                                            }}
                                            name={`NonConformanceItems.${index}.Item`}
                                            options={itemOptions[index] || []}
                                            value={
                                              (itemOptions[index] || []).find(
                                                (option) =>
                                                  option.value ===
                                                  values.NonConformanceItems[
                                                    index
                                                  ].Item
                                              ) ||
                                              (values.NonConformanceItems[index]
                                                .Item
                                                ? {
                                                    label:
                                                      values.NonConformanceItems[
                                                        index
                                                      ].Item,
                                                    value:
                                                      values.NonConformanceItems[
                                                        index
                                                      ].Item,
                                                  }
                                                : null)
                                            }
                                            onChange={(
                                              option: IReactSelectOption | null
                                            ) => {
                                              setFieldValue(
                                                `NonConformanceItems.${index}.Item`,
                                                option?.value ?? ''
                                              );
                                            }}
                                            placeholder={
                                              values.NonConformanceItems[index]
                                                .Subject
                                                ? formatMessage('please_select')
                                                : formatMessage('select_subject_first')
                                            }
                                            isClearable
                                            isSearchable={false}
                                            isDisabled={
                                              !values.NonConformanceItems[index]
                                                .Subject
                                            }
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                          />
                                          {itemTouched.Item &&
                                            itemErrors.Item && (
                                              <span className="text-danger errorMessage">
                                                {formatMessage(itemErrors.Item)}
                                              </span>
                                            )}
                                        </div>

                                        {/* Approved By */}
                                        <div className="form-group formSet col-4">
                                          <Label
                                            className="label-style col-form-label"
                                            label="approved_by"
                                            required
                                          />
                                          <Field
                                            name={`NonConformanceItems.${index}.ConfirmedBy`}
                                            as={FormikFormSelect}
                                            className="form-control"
                                            options={ReportedByOptions}
                                            setDefaultValue={true}
                                          />
                                        </div>

                                        {/* BOM Info */}
                                        <div className="form-group formSet col-xxl-9 col-8">
                                          <Label
                                            className="label-style col-form-label"
                                            label="bom_info"
                                          />
                                          <Select
                                            styles={customStyles}
                                            className="select-did-process"
                                            classNamePrefix="react-select"
                                            classNames={{
                                              option: (d) =>
                                                `our-option ${
                                                  d.isSelected ? 'active' : ''
                                                }`,
                                            }}
                                            name={`NonConformanceItems.${index}.BOMInfo`}
                                            options={materialKatashikiOptions}
                                            value={
                                              materialKatashikiOptions.find(
                                                (option) =>
                                                  option.value ===
                                                  values.NonConformanceItems[index]
                                                    .BOMInfo
                                              ) ||
                                              (values.NonConformanceItems[index]
                                                .BOMInfo
                                                ? {
                                                    label:
                                                      values.NonConformanceItems[
                                                        index
                                                      ].BOMInfo,
                                                    value:
                                                      values.NonConformanceItems[
                                                        index
                                                      ].BOMInfo,
                                                  }
                                                : null)
                                            }
                                            onChange={(
                                              option: IReactSelectOption | null
                                            ) => {
                                              setFieldValue(
                                                `NonConformanceItems.${index}.BOMInfo`,
                                                option?.value ?? ''
                                              );
                                            }}
                                            placeholder={formatMessage(
                                              'please_select'
                                            )}
                                            isClearable
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                          />
                                        </div>

                                        {/* Defective Item Count */}
                                        <div className="form-group formSet col-xxl-3 col-4">
                                          <Label
                                            className="label-style col-form-label"
                                            label="defective_item_count"
                                            required
                                          />
                                          <div className="d-flex align-items-center gap-2">
                                            <Field
                                              className="form-control"
                                              name={`NonConformanceItems.${index}.defectiveItemCount`}
                                              type="number"
                                              as={FormikFormInput}
                                            />
                                            <span>/</span>
                                            <div>
                                              {quantity ||
                                                (initialValues as any).Quantity ||
                                                0}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Defect Details */}
                                        <div className="form-group formSet col-12">
                                          <Label
                                            className="label-style col-form-label"
                                            label="defect_details"
                                            required
                                          />
                                          <Field
                                            rows={TEXTAREA_ROW_LENGTH}
                                            className="form-control"
                                            name={`NonConformanceItems.${index}.defectDetails`}
                                            type="text"
                                            as={FormikFormTextArea}
                                          />
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        disabled={
                                          values?.NonConformanceItems?.length <= 1 || itemDisabled
                                        }
                                        className="d-flex align-items-center justify-content-center border-0 rounded-circle crf-remove-btn"
                                        title="Remove"
                                      >
                                        &times;
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        </>
                      )}
                    </FieldArray>
                  </div>
                  {/* Right Column: History Section & Work Evaluation */}
                  {hasRightSection && (
                    <div
                      className={
                        showOperatorContentInHistoryPanel
                          ? 'crf-card nc-history-detail nc-history-detail-chat col-xl-7 col-xxl-7 bg-light border-start p-3 d-flex flex-column'
                          : 'crf-card nc-history-detail col-xl-8 col-xxl-8 bg-light border-start p-3 d-flex flex-column'
                      }
                    >
                      {/* Developer Comment: Only this inner region scrolls — the
                          heading, Work Evaluation, and the Operator Content composer
                          below stay put instead of the whole panel scrolling. */}
                      {displayHistoryList.length > 0 && (
                        <div className="crf-history-scroll mb-4">
                          <h6 className="text-secondary fw-bold mb-3 d-flex align-items-center gap-2 fs-7">
                            <span>
                              {formatMessage('non_conformance_work_history')}
                            </span>
                          </h6>
                          <div className="crf-timeline">
                            {displayHistoryList.map(
                              (item: any, hIndex: number) => {
                                // Developer Comment: Determine if history item is an NG record
                                const isItemNG =
                                  item.IsNG === true ||
                                  item.IsNG === 1 ||
                                  String(item.IsNG).toLowerCase() === 'true';

                                const ngReasonText = item.NGReason || '';

                                // Developer Comment: Resolve process name using ProcessName or ProcessId lookup
                                const procName =
                                  item.ProcessName ||
                                  getProcessLabel(
                                    item.ProcessId ||
                                      (initialValues as any)?.WorkProcess ||
                                      initialValues?.ReturnProcessId
                                  );

                                const workerCommentText =
                                  item.WorkerComment ||
                                  item.workerComment ||
                                  item.WorkComment ||
                                  item.workComment;

                                // Developer Comment: Same two branches as before (operator-only
                                // vs. NG/admin) — only the presentation changed to a ticket-style
                                // timeline: a centered "Work Update" divider carries the common
                                // Work Judgment/Work Process info, the operator comment renders as
                                // a conversation message (shown whenever it exists, exactly as
                                // before), and the NG verdict + reason render as the admin reply.
                                return (
                                  <div key={hIndex} className="crf-timeline-item">
                                    {isItemNG && (
                                      <TimelineNode variant="divider">
                                        <WorkInfoDivider
                                          judgment="NG"
                                          process={procName}
                                        />
                                      </TimelineNode>
                                    )}

                                    {isItemNG && (
                                      <TimelineNode variant="admin">
                                        <AdminResponse
                                          status="NG"
                                          reason={ngReasonText}
                                        />
                                      </TimelineNode>
                                    )}

                                    {workerCommentText && (
                                      <TimelineNode variant="operator">
                                        <OperatorMessage
                                          content={workerCommentText}
                                        />
                                      </TimelineNode>
                                    )}
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}

                      {/* Developer Comment: Render Work Evaluation section ONLY in VERIFICATION
                          mode. Styled the same plain, docked-composer way as Operator Content
                          (no card/border box) instead of a bordered "form" panel; Work Judgment
                          is still the same two radio inputs, just skinned as OK/NG buttons —
                          see .crf-judgement-input in the stylesheet (hand-rolled visually-hidden
                          input + sibling selector, not Bootstrap's btn-check, since this app's
                          bundled Bootstrap CSS doesn't ship btn-check's base hiding rule). */}
                      {isWorkEvaluationVisible && (() => {
                        // Developer Comment: Same condition as before (NG selected via
                        // either the numeric or string form of WorkJudgement) — just
                        // named once since it now gates two side-by-side fields plus
                        // the NG Reason field below.
                        const isNGJudgementSelected = Boolean(
                          String(values.WorkJudgement) === '1' ||
                            String(values.WorkJudgement).toUpperCase() ===
                              'NG'
                        );

                        return (
                          <div className="form-group formSet crf-operator-composer">
                            {/* Developer Comment: NG Reason required field if NG is selected */}
                            {isNGJudgementSelected && (
                              <div className="form-group formSet mt-3">
                                <Label
                                  className="label-style crf-composer-label"
                                  required={true}
                                  label="ng_reason"
                                />
                                <Field
                                  rows={2}
                                  id="NGReason"
                                  className="form-control"
                                  name="NGReason"
                                  placeholder={formatMessage('nc_ng_reason_placeholder')}
                                  type="text"
                                  maxLength={2048}
                                  as={FormikFormTextArea}
                                />
                              </div>
                            )}

                            <div className="row g-3">
                              {/* Work Judgment */}
                              <div className="col-5">
                                <Label
                                  className="label-style crf-composer-label d-block"
                                  required={true}
                                  label="work_judgement"
                                />
                                <div className="d-flex align-items-center gap-3 mt-2">
                                  {/* Option OK */}
                                  <input
                                    className="crf-judgement-input"
                                    type="radio"
                                    name="WorkJudgement"
                                    id="workJudgement_ok"
                                    value={0}
                                    autoComplete="off"
                                    checked={
                                      String(values.WorkJudgement) === '0' ||
                                      String(
                                        values.WorkJudgement
                                      ).toUpperCase() === 'OK'
                                    }
                                    onChange={() => {
                                      setFieldValue('WorkJudgement', 0);
                                    }}
                                  />
                                  <label
                                    className="crf-judgement-btn crf-judgement-btn-ok"
                                    htmlFor="workJudgement_ok"
                                  >
                                    <CheckLg aria-hidden="true" />
                                    {formatMessage('ok')}
                                  </label>

                                  {/* Option NG */}
                                  <input
                                    className="crf-judgement-input"
                                    type="radio"
                                    name="WorkJudgement"
                                    id="workJudgement_ng"
                                    value={1}
                                    autoComplete="off"
                                    checked={
                                      String(values.WorkJudgement) === '1' ||
                                      String(
                                        values.WorkJudgement
                                      ).toUpperCase() === 'NG'
                                    }
                                    onChange={() => {
                                      setFieldValue('WorkJudgement', 1);
                                    }}
                                  />
                                  <label
                                    className="crf-judgement-btn crf-judgement-btn-ng"
                                    htmlFor="workJudgement_ng"
                                  >
                                    <XLg aria-hidden="true" />
                                    {formatMessage('ng')}
                                  </label>
                                </div>
                                {touched.WorkJudgement && errors.WorkJudgement && (
                                  <span className="text-danger errorMessage d-block">
                                    {formatMessage(errors.WorkJudgement as string)}
                                  </span>
                                )}
                              </div>

                              {/* Work Process — next to Work Judgment, shown once NG is selected */}
                              {isNGJudgementSelected && (
                                <div className="col-7">
                                  <Label
                                    className="label-style crf-composer-label"
                                    required={true}
                                    label="work_process"
                                  />
                                  <Field
                                    name="WorkProcess"
                                    as={FormikFormSelect}
                                    options={workProcessOptions}
                                    setDefaultValue={true}
                                    className="form-control"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Developer Comment: Operator Content composer — moved here (out of
                          the left panel) whenever there's history to show, so it reads as
                          the reply box docked below the conversation instead of scrolling
                          away with it. Full width, not part of the scrollable chat area.
                          Hidden for the approver/verifier — they see the Work Judgment
                          buttons above instead. */}
                      {showOperatorContentInHistoryPanel &&
                        !isWorkEvaluationVisible && (
                        <div className="form-group formSet crf-operator-composer">
                          <Label
                            className="label-style crf-composer-label"
                            required={isOperatorContentEditable}
                            label="operator_content"
                          />
                          {isOperatorContentEditable ? (
                            <Field
                              rows={TEXTAREA_ROW_LENGTH}
                              id="WorkComment"
                              className="form-control"
                              name="WorkComment"
                              placeholder={formatMessage('nc_work_comment_placeholder')}
                              type="text"
                              maxLength={2048}
                              as={FormikFormTextArea}
                            />
                          ) : (
                            <div className="crf-readonly-value crf-readonly-value-multiline">
                              {values.WorkComment || '-'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Modal.Body>
              <ModalFooter
                id={Number(id)}
                onModalClosed={handleModalClose}
                isLoading={isLoading}
                saveNextBtn={'submit'}
                backCancelBtn="cancel"
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AddUpdateNonConformityForm;
